/** Quick Tally — shorthand text → Square tally rows (prototype).
 *
 *  Parses entries like:
 *      4x3x7 250 Ghana A, 4*5 x 6 100 Ghana, 7x5 25.
 *  into structured rows, tolerating notation variants (x / × / * / "by"), typos in origin
 *  ("gana" → Ghana) and quality (fuzzy against the tenant's own grade vocabulary).
 *
 *  Pure functions, zero I/O — every assumption the parser makes is recorded on the entry
 *  (`assumptions`/`warnings`) so the preview table and the grid's size-availability dot can
 *  show the user exactly what the system computed, never a silent guess.
 */

export interface QuickTallyCountry {
  name: string;
  countryCode: string;
}

export interface QuickTallyGrade {
  code: string;
  label: string;
}

export interface QuickTallyEntry {
  raw: string;
  valid: boolean;
  /** Reason the line is unusable (only when valid=false). */
  error?: 'noDimensions' | 'noPieces';
  widthIn?: number;
  thickIn?: number;
  /** Undefined = 2-dim entry, length left blank (legal in-progress row). */
  lengthFt?: number;
  pieces?: number;
  originCode?: string;
  originName?: string;
  qualityCode?: string;
  qualityLabel?: string;
  /** Human-readable notes: fuzzy matches made, length missing, unrecognized words. */
  notes: string[];
  /** True when origin or quality was matched only fuzzily (not exact). */
  fuzzy: boolean;
}

/** Case/diacritic-insensitive Levenshtein with early exit — inputs here are short words.
 *  Exported (with normalize/fuzzyFind/DIMS_RE below) so Quick Search can reuse the exact same
 *  fuzzy grammar instead of a second implementation (Mill Command Center plan, §F). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = curr;
  }
  return prev[b.length];
}

export function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export interface FuzzyResult<T> {
  item: T;
  exact: boolean;
}

/** exact → prefix → Levenshtein (≤1 for words ≤4 chars, ≤2 otherwise). Ambiguous distances lose. */
export function fuzzyFind<T>(word: string, items: T[], texts: (item: T) => string[]): FuzzyResult<T> | null {
  const w = normalize(word);
  if (w.length < 2) {
    // Single characters only ever match exactly (grade "A" etc.) — fuzzy on 1 char is noise.
    for (const item of items) {
      if (texts(item).some(t => normalize(t) === w)) return { item, exact: true };
    }
    return null;
  }
  for (const item of items) {
    if (texts(item).some(t => normalize(t) === w)) return { item, exact: true };
  }
  for (const item of items) {
    if (texts(item).some(t => normalize(t).startsWith(w) && w.length >= 3)) return { item, exact: false };
  }
  const maxDist = w.length <= 4 ? 1 : 2;
  let best: T | null = null;
  let bestDist = maxDist + 1;
  let tie = false;
  for (const item of items) {
    for (const t of texts(item)) {
      const d = levenshtein(w, normalize(t));
      if (d < bestDist) { bestDist = d; best = item; tie = false; }
      else if (d === bestDist && best !== item) { tie = true; }
    }
  }
  return best !== null && bestDist <= maxDist && !tie ? { item: best, exact: false } : null;
}

export const DIMS_RE = /(\d+(?:\.\d+)?)\s*(?:[x×*]|by)\s*(\d+(?:\.\d+)?)(?:\s*(?:[x×*]|by)\s*(\d+(?:\.\d+)?))?/i;

export function parseQuickTally(
  text: string,
  countries: QuickTallyCountry[],
  grades: QuickTallyGrade[],
): QuickTallyEntry[] {
  return (text || '')
    .split(/[,;\n]+/)
    .map(part => part.trim().replace(/\.+$/, '').trim())
    .filter(part => part.length > 0)
    .map(raw => parseEntry(raw, countries, grades));
}

function parseEntry(raw: string, countries: QuickTallyCountry[], grades: QuickTallyGrade[]): QuickTallyEntry {
  const entry: QuickTallyEntry = { raw, valid: false, notes: [], fuzzy: false };

  const dims = raw.match(DIMS_RE);
  if (!dims) {
    entry.error = 'noDimensions';
    return entry;
  }
  entry.widthIn = parseFloat(dims[1]);
  entry.thickIn = parseFloat(dims[2]);
  if (dims[3] !== undefined) {
    entry.lengthFt = parseFloat(dims[3]);
  } else {
    entry.notes.push('lengthMissing');
  }

  // Everything after the dimension token: pieces (first standalone integer), then words.
  const rest = (raw.slice(0, dims.index) + ' ' + raw.slice((dims.index || 0) + dims[0].length)).trim();
  const tokens = rest.split(/\s+/).filter(t => t.length > 0);

  const words: string[] = [];
  for (const token of tokens) {
    if (entry.pieces === undefined && /^\d+$/.test(token)) {
      entry.pieces = parseInt(token, 10);
    } else if (/[a-zA-Z]/.test(token)) {
      words.push(token.replace(/[^\p{L}\p{N}]/gu, ''));
    }
  }
  if (entry.pieces === undefined || entry.pieces < 1) {
    entry.error = 'noPieces';
    return entry;
  }
  entry.valid = true;

  // Remaining words → quality first (grades are the smaller, more specific vocabulary — a bare
  // "A" must never fuzzy-drift into a country), then origin, then flag leftovers.
  for (const word of words) {
    if (!word) continue;
    if (!entry.qualityCode) {
      const grade = fuzzyFind(word, grades, g => [g.code, g.label]);
      if (grade) {
        entry.qualityCode = grade.item.code;
        entry.qualityLabel = grade.item.label;
        if (!grade.exact) { entry.fuzzy = true; entry.notes.push(`'${word}' → ${grade.item.label}`); }
        continue;
      }
    }
    if (!entry.originCode) {
      const country = fuzzyFind(word, countries, c => [c.name, c.countryCode]);
      if (country) {
        entry.originCode = country.item.countryCode;
        entry.originName = country.item.name;
        if (!country.exact) { entry.fuzzy = true; entry.notes.push(`'${word}' → ${country.item.name}`); }
        continue;
      }
    }
    entry.notes.push(`?'${word}'`);
  }

  return entry;
}

/** One-line provenance string for the grid dot's tooltip. */
export function quickTallyProvenance(e: QuickTallyEntry): string {
  const size = e.lengthFt !== undefined
    ? `${e.widthIn}×${e.thickIn}×${e.lengthFt}` : `${e.widthIn}×${e.thickIn}×?`;
  const bits = [`${size}, ${e.pieces} pcs`];
  if (e.originName) bits.push(e.originName);
  if (e.qualityLabel) bits.push(e.qualityLabel);
  const fuzzyNote = e.fuzzy ? ' (fuzzy-matched)' : '';
  return `Quick Tally "${e.raw}" → ${bits.join(', ')}${fuzzyNote}`;
}
